function escapeRegExpLiteral(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function buildFuzzyWhitespaceRegex(snippet: string): RegExp | null {
  const words = snippet.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return null;
  }

  return new RegExp(words.map((word) => escapeRegExpLiteral(word)).join(String.raw`\s*`));
}

function collectTextNodes(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    nodes.push(node as Text);
  }

  return nodes;
}

function textNodeAtOffset(textNodes: readonly Text[], offset: number): Text | null {
  let cursor = 0;
  for (const node of textNodes) {
    const length = node.textContent?.length ?? 0;
    if (offset < cursor + length) {
      return node;
    }

    cursor += length;
  }

  return textNodes.at(-1) ?? null;
}

export function findElementForTextSnippet(root: HTMLElement, snippet: string): HTMLElement | null {
  const regex = buildFuzzyWhitespaceRegex(snippet);
  if (!regex) {
    return null;
  }

  const textNodes = collectTextNodes(root);
  const concatenated = textNodes.map((node) => node.textContent ?? "").join("");

  const match = regex.exec(concatenated);
  if (!match) {
    return null;
  }

  return textNodeAtOffset(textNodes, match.index)?.parentElement ?? null;
}

function topRelativeToContainer(element: HTMLElement, container: HTMLElement): number {
  return element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
}

export interface ScrollToTextAnchorArgs {
  readonly container: HTMLElement;
  readonly startAnchorText: string;
  readonly endAnchorText: string | null;
  readonly fraction: number;
}

export function scrollToTextAnchor({ container, startAnchorText, endAnchorText, fraction }: ScrollToTextAnchorArgs): boolean {
  const startEl = findElementForTextSnippet(container, startAnchorText);
  if (!startEl) {
    return false;
  }

  const startTop = topRelativeToContainer(startEl, container);
  const endEl = endAnchorText ? findElementForTextSnippet(container, endAnchorText) : null;
  const endTop = endEl ? topRelativeToContainer(endEl, container) : container.scrollHeight;

  const targetTop = startTop + Math.max(0, Math.min(1, fraction)) * Math.max(0, endTop - startTop);
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const desiredScrollTop = Math.min(maxScrollTop, Math.max(0, targetTop - container.clientHeight / 2));

  container.scrollTo({ top: desiredScrollTop, behavior: "smooth" });
  return true;
}
